import { useState, useCallback } from 'react';
import {
  getDocumentacionByTipoCobertura,
  getTiposCoberturaDenuncia,
} from '../../../services/Busquedas/TiposCoberturas';
import {
  getDenunciaService,
  getDenunciasService,
  saveDenunciaService,
} from '../helpers/denunciaService';
import {
  getComprobanteAutorizacion,
  getProvidenciaAutorizacion,
} from '../../../services/Afiliado/Autorizaciones';
import { get } from 'react-hook-form';

export const useDenuncias = () => {
  const [denuncias, setDenuncias] = useState([]);
  const [denuncia, setDenuncia] = useState(null);
  const [documentacionList, setDocumentacionList] = useState([]);
  const [tiposCobertura, setTiposCobertura] = useState(null);
  const [tiposDocumentacion, setTiposDocumentacion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getDenuncias = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getDenunciasService();
      setDenuncias(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const getDenuncia = useCallback(async (id) => {
    setLoading(true);
    setError(null);

    // si no viene valor en id, no hago nada
    if (!id) {
      setLoading(false);
      return;
    }

    try {
      const data = await getDenunciaService(id);
      setDenuncia(data);
      if (data?.documentacion) {
        await getDocumentacionList(data.documentacion);
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const saveDenuncia = useCallback(async (denuncia) => {
    setLoading(true);
    setError(null);
    try {
      const response = await saveDenunciaService(denuncia);
      setDenuncia(response);
      return response;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getDocumentacionList = useCallback(async (documentacion = []) => {
    setLoading(true);
    setError(null);
    try {
      if (!Array.isArray(documentacion)) return setDocumentacionList([]);
      const grupos = documentacion.reduce((acc, doc) => {
        const codigo = doc?.codigoDocumentacion;
        const key = codigo ? String(codigo) : 'sinCodigo'; // agrupa sin código bajo 'sinCodigo'

        if (!acc[key]) {
          acc[key] = [];
        }

        acc[key].push(doc);
        return acc;
      }, {});

      const result = Object.keys(grupos)
        .map((codigo) => ({ codigo, documentos: grupos[codigo] }))
        .sort((a, b) => parseInt(a.codigo) - parseInt(b.codigo));

      setDocumentacionList(result);
    } catch (err) {
      setError(err);
      setDocumentacionList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const getComprobanteDenuncia = useCallback(async (idSolicitud) => {
    setLoading(true);
    setError(null);
    try {
      const urlReporte = await getComprobanteAutorizacion({
        id: idSolicitud,
        enviarMail: false,
      });
      return urlReporte;
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const getProvidenciaDenuncia = useCallback(async (idSolicitud) => {
    setLoading(true);
    setError(null);
    try {
      const urlProvidencia = await getProvidenciaAutorizacion({
        id: idSolicitud,
      });
      return urlProvidencia;
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const getTiposDocumentacionByCobertura = useCallback(async (idTipoCobertura) => {
    setLoading(true);
    setError(null);

    if (!idTipoCobertura) {
      setTiposDocumentacion([]);
      setLoading(false);
      return;
    }

    try {
      const response = await getDocumentacionByTipoCobertura(idTipoCobertura);

      if (response) {
        const modificarNombrePropiedades = response.map((item) => ({
          ...item,
          descripcion: item.documentacion,
          codigo: item.codigoDocumentacion,
        }));
        setTiposDocumentacion(modificarNombrePropiedades);
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    denuncia,
    denuncias,
    documentacionList,
    error,
    getComprobanteDenuncia,
    getDenuncia,
    getDenuncias,
    getProvidenciaDenuncia,
    getTiposDocumentacionByCobertura,
    loading,
    saveDenuncia,
    setDenuncia,
    tiposCobertura,
    tiposDocumentacion,
  };
};
